import { Component, OnInit, OnDestroy, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  VehicleDataService,
  VehicleBrand,
  VehicleModel,
} from '../services/vehicle-data.service';

export interface VehicleSelection {
  brand: string;
  model: string;
}

@Component({
  selector: 'app-vehicle-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="vehicle-selector" [formGroup]="vehicleForm">
      <div class="form-group">
        <select
          id="vehicle-brand"
          formControlName="brand"
          class="form-control"
          [class.invalid]="
            vehicleForm.get('brand')?.invalid &&
            vehicleForm.get('brand')?.touched
          "
        >
          <option [ngValue]="null">Choose your vehicle brand</option>
          <option *ngFor="let brand of brands" [ngValue]="brand.id">
            {{ brand.name }}
          </option>
        </select>

        <div *ngIf="loadingBrands" class="loading-indicator">
          <div class="spinner"></div>
          Loading brands...
        </div>

        <div *ngIf="!loadingBrands && brands.length === 0" class="no-models">
          <small class="text-muted">Unable to load brands.</small>
        </div>

        <div
          *ngIf="
            vehicleForm.get('brand')?.invalid &&
            vehicleForm.get('brand')?.touched
          "
          class="error-message"
        >
          Brand is required
        </div>
      </div>

      <div class="form-group">
        <select
          id="vehicle-model"
          formControlName="model"
          class="form-control"
          [disabled]="!selectedBrand || loadingModels"
          [class.invalid]="
            vehicleForm.get('model')?.invalid &&
            vehicleForm.get('model')?.touched
          "
        >
          <option [ngValue]="null">{{ getModelPlaceholder() }}</option>
          <option *ngFor="let model of models" [ngValue]="model.id">
            {{ model.name }}
          </option>
        </select>

        <div *ngIf="loadingModels" class="loading-indicator">
          <div class="spinner"></div>
          Loading models...
        </div>

        <div
          *ngIf="selectedBrand && !loadingModels && models.length === 0"
          class="no-models"
        >
          <small class="text-muted">No models found.</small>
        </div>

        <div
          *ngIf="
            vehicleForm.get('model')?.invalid &&
            vehicleForm.get('model')?.touched
          "
          class="error-message"
        >
          Model is required
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./vehicle-selector.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VehicleSelectorComponent),
      multi: true,
    },
  ],
})
export class VehicleSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() required = true;

  vehicleForm: FormGroup;
  brands: VehicleBrand[] = [];
  models: VehicleModel[] = [];
  selectedBrand: number | null = null;
  loadingBrands = false;
  loadingModels = false;

  private destroy$ = new Subject<void>();
  private onChange = (value: VehicleSelection) => {};
  private onTouched = () => {};
  private pendingValue: VehicleSelection | null = null;
  private isInitializing = false;
  private isInternalUpdate = false;

  constructor(
    private fb: FormBuilder,
    private vehicleDataService: VehicleDataService
  ) {
    this.vehicleForm = this.fb.group({
      brand: [null, this.required ? Validators.required : null],
      model: [null, this.required ? Validators.required : null],
    });
  }

  ngOnInit(): void {
    this.loadBrands();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: VehicleSelection | null): void {
    this.isInitializing = true;

    if (!value || (!value.brand && !value.model)) {
      this.clearSelection();
      this.isInitializing = false;
      return;
    }

    if (!value.brand || !value.model) {
      this.clearSelection();
      this.isInitializing = false;
      return;
    }

    if (this.brands.length === 0) {
      this.pendingValue = { ...value };
      this.isInitializing = false;
      return;
    }

    this.applyValueWithSmartDetection(value);
    this.isInitializing = false;
  }

  private applyValueWithSmartDetection(value: VehicleSelection): void {
    const trimmedBrand = (value.brand || '').trim();
    const trimmedModel = (value.model || '').trim();

    if (!trimmedBrand || !trimmedModel) {
      this.clearSelection();
      return;
    }

    const normalizedBrand = this.normalizeString(trimmedBrand);

    const matchingBrand = this.brands.find(
      (b) => this.normalizeString(b.name) === normalizedBrand
    );

    if (!matchingBrand) {
      this.clearSelection();
      return;
    }

    const brandId = matchingBrand.id;
    const targetModel = trimmedModel;

    this.isInternalUpdate = true;
    this.selectedBrand = brandId;

    this.vehicleForm.patchValue({
      brand: brandId,
      model: null
    }, { emitEvent: false });

    this.isInternalUpdate = false;

    this.loadModelsAndSelectModel(brandId, targetModel);
  }

  private loadModelsAndSelectModel(brandId: number, targetModel: string): void {
    this.loadingModels = true;
    const normalizedTargetModel = this.normalizeString(targetModel);

    this.vehicleDataService
      .getModelsByBrand(brandId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (models) => {
          this.models = models;
          this.loadingModels = false;

          const matchingModel = models.find(
            (m) => this.normalizeString(m.name) === normalizedTargetModel
          );

          if (matchingModel) {
            setTimeout(() => {
              this.isInternalUpdate = true;
              this.vehicleForm.patchValue({
                model: matchingModel.id
              }, { emitEvent: false });
              this.isInternalUpdate = false;

              this.emitValue();
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading models:', error);
          this.loadingModels = false;
        },
      });
  }

  private normalizeString(str: string): string {
    return str.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  registerOnChange(fn: (value: VehicleSelection) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.vehicleForm.disable();
    } else {
      this.vehicleForm.enable();
    }
  }

  getModelPlaceholder(): string {
    if (!this.selectedBrand) return 'First select a brand';
    if (this.loadingModels) return 'Loading models...';
    return 'Choose model';
  }

  getSelectionSummary(): string {
    const value = this.vehicleForm.value;

    if (value.brand && value.model) {
      const brand = this.brands.find((b) => b.id === value.brand);
      const model = this.models.find((m) => m.id === value.model);

      if (brand && model) {
        return `${brand.name} ${model.name}`;
      }
    }

    return '';
  }

  clearSelection(): void {
    this.isInternalUpdate = true;
    this.vehicleForm.reset({
      brand: null,
      model: null,
    });
    this.isInternalUpdate = false;

    this.models = [];
    this.selectedBrand = null;
    this.pendingValue = null;

    this.emitValue();
  }

  private setupFormSubscriptions(): void {
    this.vehicleForm
      .get('brand')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((brandId) => {
        if (!this.isInternalUpdate && brandId) {
          this.onBrandChange(brandId);
        }
      });

    this.vehicleForm.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe(() => {
        if (!this.isInternalUpdate) {
          this.onTouched();
          this.emitValue();
        }
      });
  }

  private loadBrands(): void {
    this.loadingBrands = true;
    this.vehicleDataService
      .getBrands()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (brands) => {
          this.brands = brands;
          this.loadingBrands = false;

          if (this.pendingValue) {
            const valueToApply = { ...this.pendingValue };
            this.pendingValue = null;
            this.applyValueWithSmartDetection(valueToApply);
          }
        },
        error: (error) => {
          console.error('Error loading brands:', error);
          this.loadingBrands = false;
        },
      });
  }

  private onBrandChange(brandId: number): void {
    this.selectedBrand = brandId;

    this.isInternalUpdate = true;
    this.vehicleForm.get('model')?.setValue(null, { emitEvent: false });
    this.isInternalUpdate = false;

    this.models = [];

    if (brandId) {
      this.loadModels(brandId);
    }
  }

  private loadModels(brandId: number): void {
    this.loadingModels = true;
    this.vehicleDataService
      .getModelsByBrand(brandId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (models) => {
          this.models = models;
          this.loadingModels = false;
        },
        error: (error) => {
          console.error('Error loading models:', error);
          this.loadingModels = false;
          this.models = [];
        },
      });
  }

  private emitValue(): void {
    const value = this.vehicleForm.value;
    const brand = this.brands.find((b) => b.id === value.brand);
    const model = this.models.find((m) => m.id === value.model);

    const selection: VehicleSelection = {
      brand: brand?.name || '',
      model: model?.name || '',
    };

    if (
      (selection.brand && selection.model) ||
      (!selection.brand && !selection.model)
    ) {
      this.onChange(selection);
    }
  }
}
